/**
 * CMM_COMPAT — lets one bundle serve both backends while production still calls
 * the standalone CMM service, which returns payloads bare where platform wraps
 * them as `{ metadata, data }`. Mode is derived from the base URL, so flipping
 * `VITE_CERTIFIED_MATERIALS_SERVICE_URI` in a Render env group moves both the
 * host and the shape together; there is no second lever to keep in sync.
 *
 * TO REMOVE, once production is cut over and the CMM service is retired —
 * `rg CMM_COMPAT` is the full checklist:
 *   1. `cmmData(res)`            -> `res.data`
 *      `cmmPagination(d)?.total` -> `d.metadata.pagination.total`
 *      `cmmBody<T>(res.data)`    -> `res.data.data`   (v1 only)
 *   2. delete this file, `CERTIFIED_MATERIALS_ON_PLATFORM`, the test-setup env
 *      stubs and the helper tests — in BOTH frontends.
 *   3. delete `backend/src/api/handlers/certified-materials/` and its
 *      `subscriberList` block in `backend/src/2025-06/index.js`.
 *   4. drop `VITE_CERTIFIED_MATERIALS_SERVICE_URI` from every Render group so
 *      the fallback is the only path.
 *
 * Only step 3 waits on the service actually being switched off.
 */

import { CERTIFIED_MATERIALS_ON_PLATFORM } from '@/lib/constants/api';
import type {
  IApiOrdersResponse,
  IPagination,
  IPlatformEnvelope,
} from './types';

/**
 * Unwraps platform's envelope, or passes CMM's bare payload straight through.
 *
 * Typed to resolve as `T` either way so every api function keeps the return type
 * it declares and no consumer changes.
 */
export const cmmData = <T>(res: IPlatformEnvelope<T> | T): T =>
  CERTIFIED_MATERIALS_ON_PLATFORM
    ? (res as IPlatformEnvelope<T>).data
    : (res as T);

/**
 * `GET /orders`' pagination block: `metadata.pagination` on platform, top level
 * on CMM. Field names inside it are identical, so only the path moves.
 */
export const cmmPagination = (
  res: IApiOrdersResponse | undefined,
): IPagination | undefined => {
  if (!res) return undefined;
  return CERTIFIED_MATERIALS_ON_PLATFORM
    ? res.metadata?.pagination
    : (res as unknown as { pagination?: IPagination }).pagination;
};
